<script>
import { mapGetters } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const NOTIFICATION_SUMMARY_ITEMS = [
  {
    type: 'conversation_mention',
    icon: 'person',
    color: 'summary-purple',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.CONVERSATION_MENTION',
  },
  {
    type: 'conversation_assignment',
    icon: 'chat',
    color: 'summary-blue',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.CONVERSATION_ASSIGNED',
  },
  {
    type: 'task_assigned',
    icon: 'clipboard-task-list-outline',
    color: 'summary-cyan',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.TASK_ASSIGNED',
  },
  {
    type: 'task_completed',
    icon: 'checkmark-circle',
    color: 'summary-green',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.TASK_COMPLETED',
  },
  {
    type: 'task_due_tomorrow',
    icon: 'calendar',
    color: 'summary-yellow',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.TASK_DUE_TOMORROW',
  },
  {
    type: 'task_due_today',
    icon: 'calendar-clock',
    color: 'summary-orange',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.TASK_DUE_TODAY',
  },
  {
    type: 'task_overdue',
    icon: 'clock',
    color: 'summary-red',
    labelKey: 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPES.TASK_OVERDUE',
  },
];

export default {
  props: {
    isSidebarCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dismissedForSession: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      summary: 'notifications/getSummary',
    }),
    storageKey() {
      const userId = this.currentUser?.id || 'current-user';
      const accountId = this.accountId || 'current-account';
      return `${LOCAL_STORAGE_KEYS.DISMISSED_NOTIFICATION_SUMMARY_TODAY}::${accountId}::${userId}`;
    },
    todayKey() {
      const date = new Date();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    },
    isDismissedToday() {
      const storedValue = LocalStorage.get(this.storageKey);
      return storedValue?.date === this.todayKey;
    },
    visibleItems() {
      return NOTIFICATION_SUMMARY_ITEMS.map(item => ({
        ...item,
        count: Number(this.summary?.[item.type] || 0),
      })).filter(item => item.count > 0);
    },
    shouldShow() {
      return (
        this.visibleItems.length > 0 &&
        !this.dismissedForSession &&
        !this.isDismissedToday &&
        this.$route.name !== 'notifications_index'
      );
    },
  },
  watch: {
    storageKey() {
      this.dismissedForSession = false;
      this.clearExpiredPreference();
    },
  },
  mounted() {
    this.clearExpiredPreference();
  },
  methods: {
    dismissForToday() {
      LocalStorage.set(this.storageKey, { date: this.todayKey });
      this.dismissedForSession = true;
    },
    clearExpiredPreference() {
      const storedValue = LocalStorage.get(this.storageKey);
      if (storedValue?.date && storedValue.date !== this.todayKey) {
        LocalStorage.remove(this.storageKey);
      }
    },
  },
};
</script>

<template>
  <transition name="notification-summary">
    <section
      v-if="shouldShow"
      class="notification-summary-popover"
      :class="{ 'notification-summary-popover--collapsed': isSidebarCollapsed }"
    >
      <header class="notification-summary-popover__header">
        <button
          type="button"
          class="notification-summary-popover__close"
          :aria-label="$t('NOTIFICATIONS_PAGE.SUMMARY.DONT_SHOW_TODAY')"
          @click.stop="dismissForToday"
        >
          <fluent-icon icon="dismiss" size="16" />
        </button>
      </header>

      <ul class="notification-summary-popover__list">
        <li
          v-for="item in visibleItems"
          :key="item.type"
          class="notification-summary-popover__item"
        >
          <fluent-icon
            :icon="item.icon"
            size="24"
            class="notification-summary-popover__icon"
            :class="item.color"
          />
          <span
            class="notification-summary-popover__count"
            :class="item.color"
          >
            {{ item.count }}
          </span>
          <span class="notification-summary-popover__label">
            {{ $t(item.labelKey) }}
          </span>
        </li>
      </ul>
    </section>
  </transition>
</template>

<style lang="scss" scoped>
.notification-summary-popover {
  position: absolute;
  bottom: calc(100% + 0.75rem);
  left: 0;
  z-index: 220;
  width: min(24rem, calc(100vw - 1.5rem));
  color: rgb(var(--fd-body));
  // Superficie chapada. Aqui havia um gradiente 145deg do tingimento da marca
  // para o cinza da superficie, com um fallback solido por baixo. O degrade
  // fazia o topo do balao puxar azul e a base puxar cinza, e a setinha do
  // ::after repetia o mesmo trecho fora de fase.
  --notification-summary-background: rgb(var(--fd-surface-raised));
  background: var(--notification-summary-background);
  border: 1px solid rgb(var(--fd-line-strong) / 0.42);
  border-radius: 0.75rem;
}

.notification-summary-popover::after {
  position: absolute;
  bottom: -0.55rem;
  left: 1.25rem;
  width: 1.05rem;
  height: 1.05rem;
  content: '';
  background: var(--notification-summary-background);
  border-right: 1px solid rgb(var(--fd-line-strong) / 0.42);
  border-bottom: 1px solid rgb(var(--fd-line-strong) / 0.42);
  transform: rotate(45deg);
}

.notification-summary-popover__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 2.25rem;
  padding: 0.5rem 0.5rem 0;
}

.notification-summary-popover__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  color: rgb(var(--fd-body) / 0.86);
  background: transparent;
  border: 0;
}

.notification-summary-popover__close:hover {
  color: rgb(var(--fd-body));
}

.notification-summary-popover__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 0.5rem 0.5rem;
  margin: 0;
  list-style: none;
}

.notification-summary-popover__item {
  display: grid;
  grid-template-columns: 1.5rem max-content minmax(0, 1fr);
  column-gap: 0.75rem;
  row-gap: 0;
  align-items: center;
  min-height: 1.75rem;
}


.notification-summary-popover__count {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.5rem;
  text-align: left;
  white-space: nowrap;
}

.notification-summary-popover__label {
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  color: rgb(var(--fd-body) / 0.96);
}

// Uma cor por icone. O sentido destas classes e justamente diferenciar os sete
// tipos de aviso de relance -- se duas compartilham cor, o icone deixa de
// distinguir e sobra so o texto.
//
// Foi o que aconteceu em `ca54713f`, quando os hexes fixos viraram tokens: os
// sete valores distintos de 10/07/2026 (#54a1ff, #b382ff, #43d1e8, #55d98a,
// #ffe45f, #ff922f, #ff5c5c) colapsaram em quatro. `blue` e `cyan` foram os
// dois para `--fd-brand-text`, `yellow` e `orange` os dois para amber, e
// `purple` virou `--fd-subtle`, que e cinza.
//
// A volta e por matiz, nao pelo hex: cada classe pegou o degrau 800 da rampa
// mais proxima da cor original. O 800 se inverte sozinho entre os temas, coisa
// que o hex fixo nao fazia -- os originais eram claros porque o popover era
// sempre escuro; sobre a superficie clara de hoje, #ffe45f seria ilegivel.
.summary-blue {
  color: rgb(var(--fd-brand-text));
}

.summary-purple {
  color: rgb(var(--color-violet-800));
}

.summary-cyan {
  color: rgb(var(--color-teal-800));
}

.summary-green {
  color: rgb(var(--color-green-800));
}

.summary-yellow {
  color: rgb(var(--color-amber-800));
}

.summary-red {
  color: rgb(var(--color-ruby-800));
}

.summary-orange {
  color: rgb(var(--color-orange-800));
}

.notification-summary-enter-active,
.notification-summary-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.notification-summary-enter,
.notification-summary-leave-to {
  opacity: 0;
  transform: translateY(0.375rem);
}

@media (min-width: 641px) {
  .notification-summary-popover--collapsed {
    left: 0;
  }
}

@media (max-width: 640px) {
  .notification-summary-popover {
    position: fixed;
    bottom: 4.5rem;
    left: 0.875rem;
    width: calc(100vw - 1.75rem);
    max-width: 18.75rem;
  }

  .notification-summary-popover::after {
    left: 3.25rem;
  }

  .notification-summary-popover__list {
    gap: 0.625rem;
    padding: 0 0.5rem 0.5rem;
  }

  .notification-summary-popover__item {
    grid-template-columns: 1.5rem max-content minmax(0, 1fr);
    column-gap: 0.75rem;
  }

  .notification-summary-popover__label {
    font-size: 0.875rem;
    line-height: 1.15rem;
  }
}
</style>
